import { reactive, ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { AddressDto, IncidentReportType, SubmitBountyRequest } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'
import { regionConfig } from '@/config/region'
import { readApiErrorMessage } from '../../utils/api-error'
import { useWalletStore } from '../wallet'

const SUBMIT_ERROR = 'Failed to post bounty. Please try again.'

export type BountyStep = 'location' | 'dateRange' | 'details' | 'review'

const STEP_ORDER: BountyStep[] = ['location', 'dateRange', 'details', 'review']

interface BountyForm {
  location: { lat: number; lng: number } | null
  address: AddressDto | null
  windowTimezone: string | null
  windowStartDateTime: Date | null
  windowEndDateTime: Date | null
  incidentType: IncidentReportType | null
  requestDescription: string
  additionalInformation: string | null
  rewardAmountPoints: number
}

const initForm = (): BountyForm => ({
  location: null,
  address: null,
  windowTimezone: null,
  windowStartDateTime: new Date(Date.now() - 86400000),
  windowEndDateTime: new Date(Date.now() - 3600000 * 2),
  incidentType: null,
  requestDescription: '',
  additionalInformation: null,
  rewardAmountPoints: 100
})

export const useBountySubmissionStore = defineStore('bountySubmission', () => {
  const { meApi } = useApi()
  const walletStore = useWalletStore()
  const step = ref<BountyStep>('location')
  const isSubmitting = ref(false)
  const submittedBountyId = ref<string | null>(null)
  // Why the last post failed, e.g. the server's "Not enough points" (409). Null when it has not failed.
  const submitError = ref<string | null>(null)
  const form: BountyForm = reactive(initForm())

  const isLastStep = computed(() => step.value === 'review')

  function setLocation(lat: number, lng: number, timezone: string | null) {
    form.location = { lat, lng }
    form.windowTimezone = timezone
    form.address = null
  }

  function setAddress(dto: AddressDto) {
    form.address = dto
  }

  function nextStep() {
    const idx = STEP_ORDER.indexOf(step.value)
    if (idx < STEP_ORDER.length - 1) step.value = STEP_ORDER[idx + 1]
  }

  function previousStep() {
    const idx = STEP_ORDER.indexOf(step.value)
    if (idx > 0) step.value = STEP_ORDER[idx - 1]
  }

  function $reset() {
    step.value = 'location'
    isSubmitting.value = false
    submittedBountyId.value = null
    submitError.value = null
    Object.assign(form, initForm())
  }

  function buildRequest(): SubmitBountyRequest {
    if (
      !form.location ||
      !form.address ||
      !form.windowStartDateTime ||
      !form.windowEndDateTime ||
      !form.incidentType ||
      !form.windowTimezone
    ) {
      throw new Error('Bounty form is incomplete')
    }
    return {
      countryCode: regionConfig.countryCode,
      location: { type: 'Point', coordinates: [form.location.lng, form.location.lat] },
      address: form.address,
      windowStartDateTime: form.windowStartDateTime,
      windowEndDateTime: form.windowEndDateTime,
      windowTimezone: form.windowTimezone,
      incidentReportType: form.incidentType,
      requestDescription: form.requestDescription,
      additionalInformation: form.additionalInformation,
      rewardAmountPoints: form.rewardAmountPoints
    }
  }

  // The server holds the reward when the bounty is posted, so the balance has moved. A failed refresh must not undo the post.
  async function refreshWallet() {
    try {
      await walletStore.fetchWallet()
    } catch (error) {
      console.error('Failed to refresh the wallet after posting a bounty', error)
    }
  }

  async function submit(): Promise<boolean> {
    if (isSubmitting.value) return false
    isSubmitting.value = true
    submitError.value = null
    try {
      const req = buildRequest()
      const res = await meApi.meSubmitBounty({ submitBountyRequest: req })
      submittedBountyId.value = res.id!
      await refreshWallet()
      return true
    } catch (error) {
      console.error('Failed to submit bounty', error)
      submitError.value = await readApiErrorMessage(error, SUBMIT_ERROR)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    step,
    form,
    isSubmitting,
    isLastStep,
    submittedBountyId,
    submitError,
    setLocation,
    setAddress,
    nextStep,
    previousStep,
    $reset,
    submit
  }
})
